import React, { Suspense, useState } from 'react';
import {
  bool, func, number, oneOfType, string
} from 'prop-types';
import { useStore } from '@thd-nucleus/experience-context';
import { ProductLoader } from '../../ProductLoader';
import * as actions from './share.action';
import { track } from '../../../analytics';

const FRAME_HEIGHT = '436';
const FRAME_WIDTH = '626';

// eslint-disable-next-line max-len
const ShareToPinterest = React.lazy(() => import(/* webpackChunkName: "share-to-pinterest-overlay" */ './ShareToPinterest'));
// eslint-disable-next-line max-len
const ShareToEmail = React.lazy(() => import(/* webpackChunkName: "share-to-email-overlay" */ './ShareToEmail'));

const ShareBeltTooltip = ({
  collectionImageSrc,
  collectionId,
  email,
  hrefPath,
  itemId,
  pinterestTitle,
  toolTipClose
}) => {

  const [pinterestOverlayOpen, setPinterestOverlayOpen] = useState(false);
  const [emailOverlayOpen, setEmailOverlayOpen] = useState(false);
  const { storeId } = useStore();

  const closeOverlays = () => {
    setEmailOverlayOpen(false);
    setPinterestOverlayOpen(false);
  };

  const onPinImgClick = (event, data) => {
    let pinterestUrl = '';
    // The collectionImageSrc is been used as per the current
    // functionality implemented for collection
    if (collectionId) {
      pinterestUrl = 'https://pinterest.com/pin/create/button/?url=' + encodeURIComponent(hrefPath)
        + actions.generatePinCode(collectionId) + '&media=' + encodeURIComponent(collectionImageSrc) + '&description=' + encodeURIComponent(pinterestTitle); // eslint-disable-line max-len
    } else {
      const url = actions.getUrl(
        hrefPath,
        { fiscalYear: data?.product?.info?.fiscalYear },
        data?.product?.info?.productDepartment,
        data?.product?.info?.classNumber,
        itemId
      );
      pinterestUrl = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&media=${(encodeURIComponent(event.target.src))}&description=${encodeURIComponent(pinterestTitle)}`; // eslint-disable-line max-len
    }
    window.open(pinterestUrl, 'pinterest_share_dialog', `width=${FRAME_WIDTH},height=${FRAME_HEIGHT}`);
  };

  const onClickPinterest = (event, data) => {
    track({
      primaryCategory: 'interaction',
      eventName: 'pip sub-content',
      section: 'sharing',
      component: 'socialmedia',
      target: 'pinterest'
    });
    event.preventDefault();
    toolTipClose();
    if (collectionId) {
      onPinImgClick(event, data);
    } else {
      setPinterestOverlayOpen(true);
    }
  };

  const onClickFacebook = (event) => {
    event.preventDefault();
    track({
      primaryCategory: 'interaction',
      eventName: 'pip sub-content',
      section: 'sharing',
      component: 'socialmedia',
      target: 'facebook'
    });
    toolTipClose();
    const encodeURI = encodeURIComponent(hrefPath);
    const opts = `width=${FRAME_WIDTH},height=${FRAME_HEIGHT}`;
    const uri = `https://www.facebook.com/sharer/sharer.php?u=${encodeURI}, 'facebook_share_dialog', ${opts}`;
    window.open(uri);
  };

  const onClickEmail = (event) => {
    event.preventDefault();
    track({
      primaryCategory: 'interaction',
      eventName: 'pip sub-content',
      section: 'sharing',
      component: 'socialmedia',
      target: 'email'
    });
    toolTipClose();
    setEmailOverlayOpen(true);
  };

  return (
    <div
      data-section="sharing"
      data-component="socialmedia"
      className="thd-sharebelt__tooltip"
      data-socialmedia="pinterest,facebook"
    >
      <ProductLoader itemId={itemId} domains={['info']}>
        { ({ data }) => {
          return (
            <>
              <button
                type="button"
                href="#"
                onClick={(event) => onClickPinterest(event, data)}
                className="thd-sharebelt__tooltip__button"
                data-target="pinterest"
              >
                <span className="thd-sharebelt-icons thd-sharebelt-icons--pinterest" />
                Pinterest
              </button>
              {pinterestOverlayOpen && (
                <Suspense fallback={<div>Loading...</div>}>
                  <ShareToPinterest
                    closeOverlays={closeOverlays}
                    collectionId={collectionId}
                    collectionImageSrc={collectionImageSrc}
                    data={data}
                    hrefPath={hrefPath}
                    itemId={itemId}
                    pinterestOverlayOpen={pinterestOverlayOpen}
                    pinterestTitle={pinterestTitle}
                  />
                </Suspense>
              )}
            </>
          );
        }}
      </ProductLoader>
      <button
        type="button"
        href="#"
        onClick={onClickFacebook}
        className="thd-sharebelt__tooltip__button"
        data-target="facebook"
      >
        <span className="thd-sharebelt-icons thd-sharebelt-icons--facebook" />
        Facebook
      </button>
      {email && (
        <>
          <button
            type="button"
            href="#"
            onClick={(event) => onClickEmail(event)}
            className="thd-sharebelt__tooltip__button"
            data-target="email"
          >
            <span className="thd-sharebelt-icons thd-sharebelt-icons--email" />
            Email A Friend
          </button>
          {emailOverlayOpen && (
            <Suspense fallback={<div>Loading...</div>}>
              <ShareToEmail
                closeOverlays={closeOverlays}
                emailOverlayOpen={emailOverlayOpen}
                itemId={itemId}
                storeId={storeId}
              />
            </Suspense>
          )}
        </>
      )}
    </div>
  );
};

ShareBeltTooltip.displayName = 'ShareBeltTooltip';

ShareBeltTooltip.propTypes = {
  collectionImageSrc: string,
  collectionId: string,
  email: bool,
  hrefPath: string,
  itemId: oneOfType([
    string,
    number,
  ]),
  pinterestTitle: string,
  toolTipClose: func
};

ShareBeltTooltip.defaultProps = {
  collectionImageSrc: null,
  collectionId: null,
  email: false,
  hrefPath: null,
  itemId: null,
  pinterestTitle: null,
  toolTipClose: () => {}
};

export default ShareBeltTooltip;
